import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PeriodSummaryTable from '../../../placeholders/PeriodSummaryTable';
import { collectDates } from '../../../placeholders/periodSummaryUtils';
import { useUIColors } from '../../../../hooks/useTheme';
import {
  getExpenseReports,
  getJournalEntries,
  getLedger,
  getRegisterDocuments,
  getTreasuryEntries,
} from '../../../../services/treasuryAPI';

const REGISTER_SECTIONS = new Set([
  'ventas',
  'compras',
  'remuneraciones',
  'honorarios',
  'invoice',
  'rendiciones',
]);

const formatCount = (value) => Number(value || 0).toLocaleString('es-CL');
const formatMoney = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const normalizeRegisterRow = (section, row) => {
  if (section !== 'rendiciones') return row;
  return {
    ...row,
    doc_date: row.report_date,
    document_type: row.report_type,
    counterpart_name: row.requester_name,
    counterpart_rut: row.requester_rut,
  };
};

const getRowDate = (section, row) => {
  if (REGISTER_SECTIONS.has(section)) return row.doc_date;
  return row.entry_date;
};

const getRowSegment = (section, row) => {
  if (REGISTER_SECTIONS.has(section)) {
    return String(row.document_type || row.status || 'Sin tipo').trim() || 'Sin tipo';
  }
  if (section === 'mayor') {
    return 'all';
  }
  if (section === 'treasury') {
    return [row.bank_name, row.account_name].filter(Boolean).join(' · ') || 'Sin cuenta';
  }
  return String(row.status || 'Sin estado').trim() || 'Sin estado';
};

const usesSharedDocuments = (section) =>
  (REGISTER_SECTIONS.has(section) && section !== 'rendiciones') || section === 'treasury';

const signedTreasuryAmount = (row) =>
  (String(row.entry_type || '') === 'income' ? 1 : -1) * Number(row.amount || 0);

/**
 * Tabla resumen por período (mismo modelo que Ventas) según el libro activo.
 * El selector de período vive en el workspace de Contabilidad.
 */
const AccountingPeriodSummary = ({
  section = 'ventas',
  refreshKey = 0,
  selectedPeriod = null,
  periodTitle = 'Resumen contable',
  documents = null,
  documentsLoading = false,
  documentsError = '',
}) => {
  const ui = useUIColors();
  const [rowsData, setRowsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [summaryCollapsed, setSummaryCollapsed] = useState(false);
  const [summaryMetric, setSummaryMetric] = useState('amount');
  const [segmentVisibility, setSegmentVisibility] = useState({});

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      let payload = [];
      if (REGISTER_SECTIONS.has(section)) {
        let raw;
        if (section === 'rendiciones') {
          raw = await getExpenseReports();
        } else if (documents !== null) {
          raw = documents;
        } else {
          raw = await getRegisterDocuments({ register_type: section });
        }
        payload = (Array.isArray(raw) ? raw : []).map((row) => normalizeRegisterRow(section, row));
      } else if (section === 'asientos') {
        payload = await getJournalEntries();
      } else if (section === 'diario') {
        payload = await getJournalEntries({ status: 'Contabilizado' });
      } else if (section === 'mayor') {
        payload = await getLedger();
      } else if (section === 'treasury') {
        payload = documents !== null ? documents : await getTreasuryEntries();
      }
      setRowsData(Array.isArray(payload) ? payload : []);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el resumen.');
      setRowsData([]);
    } finally {
      setIsLoading(false);
    }
  }, [section, documents]);

  useEffect(() => {
    if (usesSharedDocuments(section) && documents !== null) {
      if (documentsLoading) {
        setIsLoading(true);
        return;
      }
      if (documentsError) {
        setError(documentsError);
        setRowsData([]);
        setIsLoading(false);
        return;
      }
    }
    load();
  }, [load, refreshKey, documents, documentsLoading, documentsError, section]);

  const segmentOptions = useMemo(() => {
    if (section === 'mayor') return [];
    const set = new Set();
    rowsData.forEach((row) => set.add(getRowSegment(section, row)));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [rowsData, section]);

  useEffect(() => {
    if (!segmentOptions.length) return;
    setSegmentVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      segmentOptions.forEach((segment) => {
        if (next[segment] === undefined) {
          next[segment] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [segmentOptions]);

  const summaryRows = useMemo(() => {
    if (!selectedPeriod) return [];
    const periods = selectedPeriod.columns || [];
    const periodStart = periods[0]?.start || selectedPeriod.range?.start || null;
    const scoped = rowsData.filter(
      (row) => segmentVisibility[getRowSegment(section, row)] !== false
    );

    const inBucket = (row, bucket) => {
      const date = collectDates([row], (item) => getRowDate(section, item))[0];
      return Boolean(date && date >= bucket.start && date <= bucket.end);
    };
    const upToBucket = (row, bucket) => {
      const date = collectDates([row], (item) => getRowDate(section, item))[0];
      if (!date || date > bucket.end) return false;
      if (periodStart && date < periodStart) return false;
      return true;
    };

    if (REGISTER_SECTIONS.has(section)) {
      const countBy = (predicate) =>
        periods.map((bucket) => scoped.filter((row) => inBucket(row, bucket) && predicate(row)).length);
      const sumBy = (field, predicate = () => true) =>
        periods.map((bucket) =>
          scoped
            .filter((row) => inBucket(row, bucket) && predicate(row))
            .reduce((acc, row) => acc + Number(row[field] || 0), 0)
        );
      const cumSumBy = (field) =>
        periods.map((bucket) =>
          scoped
            .filter((row) => upToBucket(row, bucket))
            .reduce((acc, row) => acc + Number(row[field] || 0), 0)
        );
      const cumCount = () =>
        periods.map((bucket) => scoped.filter((row) => upToBucket(row, bucket)).length);

      if (summaryMetric === 'count') {
        if (section === 'ventas') {
          const collectionOf = (row) => String(row.collection_status || row.status || '').trim();
          return [
            { label: 'Documentos', tone: 'client', values: countBy(() => true).map(formatCount) },
            {
              label: 'Pagadas',
              tone: 'neutral',
              values: countBy((row) => collectionOf(row) === 'Pagada').map(formatCount),
            },
            {
              label: 'Por vencer',
              tone: 'prospect',
              values: countBy((row) => collectionOf(row) === 'Por vencer').map(formatCount),
            },
            {
              label: 'Vencidas',
              tone: 'neutral',
              values: countBy((row) => collectionOf(row) === 'Vencida').map(formatCount),
            },
            { label: 'Documentos acum. período', tone: 'total', values: cumCount().map(formatCount) },
          ];
        }
        return [
          { label: 'Documentos', tone: 'client', values: countBy(() => true).map(formatCount) },
          {
            label: 'Contabilizados',
            tone: 'neutral',
            values: countBy((row) => String(row.status || '') === 'Contabilizado').map(formatCount),
          },
          {
            label: 'Registrados',
            tone: 'prospect',
            values: countBy((row) => String(row.status || '') === 'Registrado').map(formatCount),
          },
          {
            label: 'Anuladas',
            tone: 'neutral',
            values: countBy((row) => String(row.status || '') === 'Anulada').map(formatCount),
          },
          { label: 'Documentos acum. período', tone: 'total', values: cumCount().map(formatCount) },
        ];
      }

      return [
        { label: 'Neto (CLP)', tone: 'client', values: sumBy('net_amount').map(formatMoney) },
        { label: 'IVA (CLP)', tone: 'neutral', values: sumBy('tax_amount').map(formatMoney) },
        { label: 'Total (CLP)', tone: 'prospect', values: sumBy('total_amount').map(formatMoney) },
        { label: 'Total acum. período (CLP)', tone: 'total', values: cumSumBy('total_amount').map(formatMoney) },
      ];
    }

    if (section === 'mayor') {
      const debe = periods.map((bucket) =>
        scoped
          .filter((row) => inBucket(row, bucket) && String(row.side || '') === 'debe')
          .reduce((acc, row) => acc + Number(row.amount || 0), 0)
      );
      const haber = periods.map((bucket) =>
        scoped
          .filter((row) => inBucket(row, bucket) && String(row.side || '') === 'haber')
          .reduce((acc, row) => acc + Number(row.amount || 0), 0)
      );
      const countMoves = periods.map((bucket) => scoped.filter((row) => inBucket(row, bucket)).length);
      const cumMoves = periods.map((bucket) => scoped.filter((row) => upToBucket(row, bucket)).length);

      if (summaryMetric === 'count') {
        return [
          { label: 'Movimientos', tone: 'client', values: countMoves.map(formatCount) },
          { label: 'Movimientos acum. período', tone: 'total', values: cumMoves.map(formatCount) },
        ];
      }
      return [
        { label: 'DEBE (CLP)', tone: 'client', values: debe.map(formatMoney) },
        { label: 'HABER (CLP)', tone: 'neutral', values: haber.map(formatMoney) },
        {
          label: 'Movimientos (CLP)',
          tone: 'prospect',
          values: periods.map((_, i) => debe[i] + haber[i]).map(formatMoney),
        },
      ];
    }

    if (section === 'treasury') {
      const countBy = (predicate) =>
        periods.map((bucket) => scoped.filter((row) => inBucket(row, bucket) && predicate(row)).length);
      const sumBy = (valueOf, predicate = () => true) =>
        periods.map((bucket) =>
          scoped
            .filter((row) => inBucket(row, bucket) && predicate(row))
            .reduce((acc, row) => acc + valueOf(row), 0)
        );
      const isIncome = (row) => String(row.entry_type || '') === 'income';

      if (summaryMetric === 'count') {
        return [
          { label: 'Movimientos', tone: 'client', values: countBy(() => true).map(formatCount) },
          {
            label: 'Conciliados',
            tone: 'neutral',
            values: countBy((row) => row.reconciliation_status === 'Conciliado').map(formatCount),
          },
          {
            label: 'Pendientes',
            tone: 'prospect',
            values: countBy((row) => row.reconciliation_status !== 'Conciliado').map(formatCount),
          },
          {
            label: 'Movimientos acum. período',
            tone: 'total',
            values: periods
              .map((bucket) => scoped.filter((row) => upToBucket(row, bucket)).length)
              .map(formatCount),
          },
        ];
      }

      const amountOf = (row) => Number(row.amount || 0);
      return [
        { label: 'Ingresos (CLP)', tone: 'client', values: sumBy(amountOf, isIncome).map(formatMoney) },
        {
          label: 'Egresos (CLP)',
          tone: 'neutral',
          values: sumBy(amountOf, (row) => !isIncome(row)).map(formatMoney),
        },
        { label: 'Flujo neto (CLP)', tone: 'prospect', values: sumBy(signedTreasuryAmount).map(formatMoney) },
        {
          label: 'Flujo neto acum. período (CLP)',
          tone: 'total',
          values: periods
            .map((bucket) =>
              scoped
                .filter((row) => upToBucket(row, bucket))
                .reduce((acc, row) => acc + signedTreasuryAmount(row), 0)
            )
            .map(formatMoney),
        },
      ];
    }

    const countBy = (predicate) =>
      periods.map((bucket) => scoped.filter((row) => inBucket(row, bucket) && predicate(row)).length);
    const sumField = (field) =>
      periods.map((bucket) =>
        scoped
          .filter((row) => inBucket(row, bucket))
          .reduce((acc, row) => acc + Number(row[field] || 0), 0)
      );
    const cumCount = () =>
      periods.map((bucket) => scoped.filter((row) => upToBucket(row, bucket)).length);
    const cumSumField = (field) =>
      periods.map((bucket) =>
        scoped
          .filter((row) => upToBucket(row, bucket))
          .reduce((acc, row) => acc + Number(row[field] || 0), 0)
      );

    if (summaryMetric === 'count') {
      return [
        { label: 'Asientos', tone: 'client', values: countBy(() => true).map(formatCount) },
        {
          label: 'Contabilizados',
          tone: 'neutral',
          values: countBy((row) => String(row.status || '') === 'Contabilizado').map(formatCount),
        },
        {
          label: 'Borrador',
          tone: 'prospect',
          values: countBy((row) => String(row.status || '') === 'Borrador').map(formatCount),
        },
        { label: 'Asientos acum. período', tone: 'total', values: cumCount().map(formatCount) },
      ];
    }

    return [
      { label: 'Total DEBE (CLP)', tone: 'client', values: sumField('total_debe').map(formatMoney) },
      { label: 'Total HABER (CLP)', tone: 'neutral', values: sumField('total_haber').map(formatMoney) },
      {
        label: 'DEBE acum. período (CLP)',
        tone: 'total',
        values: cumSumField('total_debe').map(formatMoney),
      },
    ];
  }, [rowsData, section, selectedPeriod, summaryMetric, segmentVisibility]);

  const metricOptions = [
    { value: 'amount', label: 'Montos (CLP)' },
    { value: 'count', label: 'Cantidades' },
  ];

  const segmentFilterLabel = REGISTER_SECTIONS.has(section)
    ? 'Tipo doc.'
    : section === 'treasury'
      ? 'Cuenta'
      : 'Estado';

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {error ? <p style={{ margin: 0, color: ui.danger, fontSize: 12 }}>{error}</p> : null}
      {isLoading ? (
        <p style={{ margin: 0, color: ui.muted, fontSize: 12 }}>Cargando resumen…</p>
      ) : (
        <PeriodSummaryTable
          periods={selectedPeriod?.columns || []}
          rows={summaryRows}
          title={periodTitle}
          collapsed={summaryCollapsed}
          onToggleCollapsed={() => setSummaryCollapsed((prev) => !prev)}
          segmentOptions={segmentOptions}
          segmentVisibility={segmentVisibility}
          onToggleSegment={(segment) =>
            setSegmentVisibility((prev) => ({ ...prev, [segment]: prev[segment] === false }))
          }
          onShowAllSegments={() =>
            setSegmentVisibility(
              Object.fromEntries(segmentOptions.map((segment) => [segment, true]))
            )
          }
          onHideAllSegments={() =>
            setSegmentVisibility(
              Object.fromEntries(segmentOptions.map((segment) => [segment, false]))
            )
          }
          segmentFilterLabel={segmentFilterLabel}
          metricOptions={metricOptions}
          metric={summaryMetric}
          onMetricChange={setSummaryMetric}
        />
      )}
    </div>
  );
};

export default AccountingPeriodSummary;
